import type {AnyObject} from 'src/typings';
import type {IHttpParamSerializer, IWindowService} from 'angular';
import {isPartOfBaseURL} from '../utils/url-utils';
import {PopupBlockerError} from '../errors/popup-blocker.error';

const SPA_PREFIX = '/#!';

/**
 * @ngdoc service
 * @name walker
 * @module portaro.services
 *
 * @required $httpParamSerializerJQLike
 * @required $window
 *
 * @description
 * Factory provides method for URL handling.
 */
export default class WalkerService {
    public static serviceName = 'walker'; // texas ranger

    /*@ngInject*/
    constructor(private $httpParamSerializer: IHttpParamSerializer, private $window: IWindowService) {
    }

    /**
     * @ngdoc method
     * @name walker#thisPage
     *
     * @description
     * Reload page without parameters after `#` sign.
     */
    public thisPage(): Promise<string> {
        this.$window.location.href = this.getCurrentPageUrl();
        return Promise.resolve(this.$window.location.href);
    }

    /**
     * @ngdoc method
     * @name walker#newPage
     *
     * @param {string} path Base path
     * @param {*=} params Params which will be serialized
     *
     * @description
     * Go to new page with given params
     */
    public newPage(path: string, params?: AnyObject): Promise<string> {
        this.$window.location.href = this.buildUrl(path, params);
        return Promise.resolve(this.$window.location.href);
    }

    /**
     * @ngdoc method
     * @name walker#newSpaPage
     *
     * @param {string} path
     * @param {*=} params
     *
     * @description
     * Go to SPA page described only SPA URI.
     * Final URL is concat of `/!#` + `path`
     */
    public newSpaPage(path: string, params?: AnyObject): Promise<string | Window> {
        return Promise.resolve(this.newPage(SPA_PREFIX + path, params));
    }

    /**
     * @ngdoc method
     * @name walker#newPageInNewWindow
     * @param {string} path URL
     * @param {*=} params Parameters
     *
     * @description
     * Go to specified URL with optional parameters in new tab.
     */
    public newPageInNewWindow(path: string, params: AnyObject = {}): Promise<Window> {
        return this.openWindow(path, params, '_blank');
    }

    /**
     * @ngdoc method
     * @name walker#newPageInNewWindow
     * @param {string} path URL
     * @param {*=} params Parameters
     *
     * @description
     * Go to specified URL with optional parameters in new popup window.
     */
    public newPageInPopup(path: string, params: AnyObject = {}): Promise<Window> {
        return this.openWindow(path, params, 'targetWindow', 'menubar=0,resizable=1,width=1000,height=800');
    }

    /**
     * @ngdoc method
     * @name walker#newPageInNewWindow
     * @param {string} path URL
     * @param {*=} params Parameters
     * @param target e.g. 'targetWindow' or '_blank'
     * @param features e.g. 'menubar=0,resizable=1,width=1000,height=800'
     * @description
     * Go to specified URL with optional parameters in new popup window.
     */
    private openWindow(path: string, params: AnyObject = {}, target: string, features?: string): Promise<Window> {
        let fullPath = path;
        if (params) {
            fullPath += `?${this.$httpParamSerializer(params)}`;
        }

        const openedWindow = this.$window.open(fullPath, target, features);

        if (!openedWindow || openedWindow.closed || typeof openedWindow.closed === 'undefined') {
            return Promise.reject(new PopupBlockerError('Unable to redirect page to new window.'));
        }

        return Promise.resolve(openedWindow);
    }

    /**
     * @ngdoc method
     * @name walker#newPageDetectNewWindow
     * @param {string} path URL
     * @param {*=} params Parameters
     *
     * @description
     * Open link in new tab, if it is pointing to another web otherwise only redirect.
     */
    public newPageDetectNewWindow(path: string, params: AnyObject = {}): Promise<string | Window> {
        if (isPartOfBaseURL(path)) {
            return this.newPage(path, params);
        } else {
            return this.newPageInNewWindow(path, params);
        }
    }

    /**
     * @ngdoc method
     * @name walker#newPageWithoutCurrentPageHistory
     *
     * @param {string} path URL
     *
     * @description
     * Redirect on new URL without record history
     */
    public newPageWithoutCurrentPageHistory(path: string): Promise<string> {
        // see http://stackoverflow.com/questions/503093/how-do-i-redirect-to-another-page-in-jquery
        this.$window.location.replace(path);
        return Promise.resolve(this.$window.location.href);
    }

    /**
     * @private
     * @returns {string}
     * @description
     * Return URL without section after `#` sign
     */
    private getCurrentPageUrl(): string {
        let url = window.location.href;
        const indexOfSharp = url.indexOf('#');
        if (indexOfSharp > 0) {
            url = url.substring(0, indexOfSharp);
        }
        return url;
    }

    /**
     * @private
     * @param {string} path Base path
     * @param {*=} params Params to serialization
     * @returns {string}
     *
     * @description
     * Create new URL by concat path with serialized params
     */
    private buildUrl(path: string, params: AnyObject): string {
        let queryParams = '';
        if (params) {
            queryParams = `?${this.$httpParamSerializer(params)}`;
        }
        return path + queryParams;
    }
}
